import { Box, Option } from "@mui/joy";
import { hrAccentColor, textTokens } from "styles/globalStyles";
import DarkTypography from "components/common/DarkTypography";
import DarkInput from "components/common/DarkInput";
import DarkSelect from "components/common/DarkSelect";
import DarkCard from "components/common/DarkCard";
import IconButtonCTA from "components/common/IconButtonCTA";
import { SCreate, SDelete } from "styles/StyledMUI";
import type { ParamsTemplateEntry, ParamValueType } from "./paramsTemplateUtils";

export function ParamsTemplateEntryRow({
  entry,
  onChange,
  onRemove,
  disabled,
}: {
  entry: ParamsTemplateEntry;
  onChange: (entry: ParamsTemplateEntry) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        alignItems: "flex-start",
        p: 1,
        borderRadius: 1,
        bgcolor: "rgba(255,255,255,0.04)",
      }}
    >
      <DarkInput
        placeholder="Clé"
        value={entry.key}
        onChange={(e) => onChange({ ...entry, key: e.target.value })}
        disabled={disabled}
        sx={{ flex: { xs: "1 1 100%", sm: "1 1 140px" }, minWidth: 0 }}
        slotProps={{ input: { sx: { color: textTokens.color.primary } } }}
      />
      <DarkSelect
        value={entry.type}
        onChange={(_, v) => v && onChange({ ...entry, type: v as ParamValueType })}
        disabled={disabled}
        sx={{ flex: { xs: "1 1 100%", sm: "0 0 120px" }, minWidth: 0 }}
      >
        <Option value="string">Texte</Option>
        <Option value="number">Nombre</Option>
        <Option value="boolean">Oui/Non</Option>
        <Option value="users">Utilisateurs</Option>
        <Option value="customers">Clients</Option>
        <Option value="guides">Encadrants</Option>
      </DarkSelect>
      {!disabled && (
        <IconButtonCTA
          aria-label="Supprimer"
          ctaVariant="danger"
          onClick={onRemove}
          sx={{ flexShrink: 0 }}
        >
          <SDelete />
        </IconButtonCTA>
      )}
    </Box>
  );
}

export function ParamsTemplateSection({
  title,
  entries,
  onAdd,
  onUpdate,
  onRemove,
  canEdit,
  disabled,
  wrapInCard = true,
}: {
  title: string;
  entries: ParamsTemplateEntry[];
  onAdd: () => void;
  onUpdate: (index: number, entry: ParamsTemplateEntry) => void;
  onRemove: (index: number) => void;
  canEdit: boolean;
  disabled: boolean;
  wrapInCard?: boolean;
}) {
  const content = (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <DarkTypography level="title-sm" sx={{ color: hrAccentColor }}>
          {title}
        </DarkTypography>
        {canEdit && (
          <IconButtonCTA aria-label="Ajouter une clé-valeur" ctaVariant="accent" onClick={onAdd}>
            <SCreate />
          </IconButtonCTA>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxHeight: { xs: "30vh", sm: "35vh" },
          overflowY: "auto",
        }}
      >
        {entries.length === 0 ? (
          <DarkTypography level="body-sm" sx={{ color: "rgba(255,255,255,0.6)" }}>
            Aucune clé.
          </DarkTypography>
        ) : (
          entries.map((entry, idx) => (
            <ParamsTemplateEntryRow
              key={idx}
              entry={entry}
              onChange={(e) => onUpdate(idx, e)}
              onRemove={() => onRemove(idx)}
              disabled={disabled}
            />
          ))
        )}
      </Box>
    </>
  );
  return wrapInCard ? <DarkCard dense>{content}</DarkCard> : <Box>{content}</Box>;
}
